import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { isSafeHttpUrl } from '../../core/utils/isSafeHttpUrl';
import type { PopupBannerCta } from './popupBanner.types';

export type CtaOutcome = 'internal' | 'external-web' | 'external-native' | 'rejected';

/**
 * Opens a banner CTA's target according to its `actionType`, and reports which route it took.
 *
 * `internal` means the target stays inside MyApps, so on native it must stay in the Capacitor
 * WebView — `window.open(_blank)` there strands the user in a chromeless view with no way back.
 * `external` leaves MyApps, so on native it goes to the system browser and on the web to a new tab.
 *
 * The URL is validated first: it comes from the CMS, so an unsafe or malformed one is refused
 * rather than opened. Refusing returns `rejected` and opens nothing; the caller still closes the
 * banner, because the user did interact with it.
 *
 * An `internal` CTA must also resolve to this origin. `isSafeHttpUrl` deliberately lets an absolute
 * URL point anywhere — the CMS owns external targets — so without this an author who mismarks an
 * off-origin URL as `internal` would be navigated to that URL's path on our own origin.
 */
export const openPopupBannerCta = (
  cta: PopupBannerCta,
  navigate: (path: string) => void,
): CtaOutcome => {
  if (!isSafeHttpUrl(cta.url)) return 'rejected';

  if (cta.actionType === 'internal') {
    const target = new URL(cta.url, window.location.origin);
    if (target.origin !== window.location.origin) return 'rejected';
    navigate(`${target.pathname}${target.search}${target.hash}`);
    return 'internal';
  }

  if (Capacitor.isNativePlatform()) {
    void Browser.open({ url: cta.url });
    return 'external-native';
  }

  window.open(cta.url, '_blank', 'noopener,noreferrer');
  return 'external-web';
};
